import api from './api';

// ── Types ────────────────────────────────────────────────────────

export interface EggWatchProgramHealth {
  active_farms: number;
  logged_today: number;
  logging_rate_pct: number;
  avg_lay_rate: number | null;
  total_hens_tracked: number;
}

export interface EggWatchFarmSummary {
  id: number;
  name: string;
  email: string | null;
  location: string | null;
  hen_count: number;
  flock_count: number;
  feed_formula: string | null;
  current_lay_rate: number | null;
  baseline_lay_rate: number | null;
  trend: 'up' | 'down' | 'stable' | 'unknown';
  trend_pct: number | null;
  last_log_date: string | null;
  days_since_log: number;
  status: 'excellent' | 'good' | 'attention' | 'not_logging';
  active_alerts: number;
}

export interface EggWatchFarmDetail {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  location: { city: string | null; state: string | null; lat: number | null; lon: number | null };
  flocks: Array<{
    id: string;
    name: string;
    breed: string | null;
    breed_display: string | null;
    quantity: number;
    hatch_date: string | null;
    age_weeks: number | null;
    production_phase: string | null;
    peak_lay_rate: number | null;
  }>;
  current_lay_rate: number | null;
  baseline_lay_rate: number | null;
  days_of_data: number;
  current_feed: { formula_id: number | null; formula_name: string | null; started_date: string | null } | null;
  recent_orders: Array<{ id: number; order_date: string; total: string; items_summary: string }>;
  daily_logs: Array<{
    date: string;
    egg_count: number;
    hen_count: number;
    lay_rate: number;
    feed_lbs: number | null;
    water_gallons: number | null;
    tags: string[];
  }>;
  weather_events: Array<{ date: string; event_type: string; impact_pct: number | null }>;
  alerts: Array<{ id: string; alert_type: string; severity: string; created_at: string; acknowledged_at: string | null }>;
  insights: Array<{ type: 'opportunity' | 'concern' | 'info'; title: string; detail: string }>;
}

export interface EggWatchAdminAlert {
  id: string;
  farm_id: number;
  farm_name: string;
  alert_type: string;
  severity: string;
  title: string;
  details: Record<string, any>;
  status: string;
  created_at: string;
  reviewed_at: string | null;
  action_taken: string | null;
}

export interface FeedFormulaPerformance {
  formula_id: number;
  formula_name: string;
  farms_using: number;
  total_hens: number;
  avg_lay_rate: number;
  median_lay_rate: number;
  vs_expected: number;
  trend: 'up' | 'down' | 'stable';
  heat_recovery_days: number | null;
  cold_recovery_days: number | null;
  breed_performance: Record<string, number>;
}

export interface EggWatchAdminSummary {
  program_health: EggWatchProgramHealth;
  alerts: { production_drops: number; not_logging: number; total_attention_needed: number };
  opportunities: { feed_reorders: number; weather_prep: number; total: number };
  feed_performance: FeedFormulaPerformance[];
  recent_activity: Array<{ farm_name: string; action: string; detail: string; timestamp: string }>;
}

// ── API Functions ────────────────────────────────────────────────

const BASE = '/planner/admin/egg-watch';

export const eggWatchAdminAPI = {
  getSummary: () =>
    api.get<EggWatchAdminSummary>(`${BASE}/summary/`),

  getFarms: (params?: { search?: string; status?: string; feed_formula?: string }) =>
    api.get<EggWatchFarmSummary[]>(`${BASE}/farms/`, { params }),

  getFarmDetail: (farmId: number) =>
    api.get<EggWatchFarmDetail>(`${BASE}/farms/${farmId}/`),

  getFeedPerformance: (params?: { period?: string }) =>
    api.get<FeedFormulaPerformance[]>(`${BASE}/feed-performance/`, { params }),

  getAdminAlerts: (params?: { alert_type?: string; status?: string; limit?: number }) =>
    api.get<EggWatchAdminAlert[]>(`${BASE}/alerts/`, { params }),

  exportData: (params: { export_type: string; start_date?: string; end_date?: string; format?: string }) =>
    api.get(`${BASE}/export/`, { params, responseType: 'blob' }),
};

export default eggWatchAdminAPI;
